import React from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { ArrowLeft, Blocks, Lock, Mail, MessageCircle, MessageSquare, MessageSquareText } from 'lucide-react';
import { useStore } from '../../../context/StoreContext';
import useAccess from '../../../hooks/useAccess';
import styles from './IntegrationsLayout.module.css';

// =============================================================================
// Integrations: WhatsApp, SMS, Email.  Module "integrations" in the access blueprint.
//   view          open this page and see which connections are on
//   view_<x>      see that connection's IDs (tokens / passwords never shown)
//   edit_<x>      change that connection
// =============================================================================
const TABS = [
    { id: 'whatsapp', label: 'WhatsApp', sub: 'Meta Cloud API', icon: MessageSquare, path: '/integrations/whatsapp' },
    { id: 'whatsapp-messages', label: 'WhatsApp messages', sub: 'Templates for each message', icon: MessageSquareText, path: '/integrations/whatsapp-messages' },
    { id: 'sms', label: 'SMS gateway', sub: 'Sender ID and provider', icon: MessageCircle, path: '/integrations/sms' },
    { id: 'email', label: 'Email (SMTP)', sub: 'Mail server', icon: Mail, path: '/integrations/email' },
];

export default function IntegrationsLayout() {
    const navigate = useNavigate();
    const { activeStore } = useStore();
    const { can, isOwner } = useAccess();
    const isHQ = activeStore?.isHQ === true || (activeStore?._id || activeStore?.id) === 'global_hq';

    const levelOf = (id) => {
        if (isOwner || can(`integrations.edit_${id}`)) return 'edit';
        if (can(`integrations.view_${id}`)) return 'view';
        return 'status';
    };

    return (
        <div className={styles.layout}>
            <aside className={styles.side}>
                <div className={styles.sideHead}>
                    <button type="button" className={styles.back} onClick={() => navigate('/settings')}>
                        <ArrowLeft size={13} /> Settings
                    </button>
                    <h1><Blocks size={17} /> Integrations</h1>
                    <p>{isHQ ? 'Master connections used by every branch that follows HQ.' : `Connections for ${activeStore?.storeName || 'this branch'}.`}</p>
                </div>

                <nav className={styles.nav} aria-label="Integrations">
                    <span className={styles.navLabel}>Messaging</span>
                    {TABS.map(({ id, label, sub, icon: Icon, path }) => {
                        const level = levelOf(id);
                        return (
                            <NavLink key={id} to={path} className={({ isActive }) => `${styles.item} ${isActive ? styles.itemOn : ''}`}>
                                <span className={styles.itemIcon}><Icon size={15} /></span>
                                <span className={styles.itemText}>
                                    <strong>{label}</strong>
                                    <small>{sub}</small>
                                </span>
                                {level === 'view' && <span className={styles.tag}>View only</span>}
                                {level === 'status' && <span className={styles.tagLock} title="Keys are hidden for your login"><Lock size={10} /></span>}
                            </NavLink>
                        );
                    })}
                </nav>

                <p className={styles.sideFoot}>
                    Tokens and passwords are never shown after saving. Paste a new one to replace it.
                </p>
            </aside>

            <main className={styles.content}>
                <Outlet />
            </main>
        </div>
    );
}
